import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Check, 
  Crown, 
  Loader2, 
  Sparkles,
  Star,
  Zap,
  Shield,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';

const plans = [
  {
    id: 'pack5',
    name: 'Essencial',
    price: 19.90,
    yearlyPrice: 199.00,
    priceDisplay: '19,90',
    period: '/mês',
    features: ['5 leituras mensais', 'Mapa reflexológico básico', 'Recomendações gerais', 'Histórico de leituras'],
    icon: Sparkles,
    popular: false,
    color: '#4DB6AC'
  },
  {
    id: 'detailed10',
    name: 'Plus',
    price: 49.90,
    yearlyPrice: 499.00,
    priceDisplay: '49,90',
    period: '/mês',
    features: ['10 leituras mensais', 'Análise detalhada por zonas', 'Score de reatividade', 'Protocolo personalizado', 'Suporte por email'],
    icon: Star,
    popular: true,
    color: '#D4A373'
  },
  {
    id: 'advanced',
    name: 'Premium',
    price: 99.00,
    yearlyPrice: 990.00,
    priceDisplay: '99,00',
    period: '/mês',
    features: ['20 leituras mensais', 'Análise completa', 'Histórico evolutivo', 'Recomendações avançadas', 'Relatório visual compartilhável', 'Suporte prioritário'],
    icon: Zap,
    popular: false,
    color: '#2E5C55'
  }
];

const PlansPage = () => {
  const { subscription, api, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Check for payment redirect
  useEffect(() => {
    const payment = searchParams.get('payment');
    const paymentId = searchParams.get('payment_id');
    const status = searchParams.get('status');
    const externalRef = searchParams.get('external_reference');
    
    if (payment === 'success' || status === 'approved') {
      setPaymentStatus('success');
      verifyPayment(paymentId, status, externalRef);
    } else if (payment === 'failure' || status === 'rejected') {
      setPaymentStatus('failure');
      setError('Pagamento não aprovado. Por favor, tente novamente.');
    } else if (payment === 'pending' || status === 'pending') {
      setPaymentStatus('pending');
      setSuccess('Pagamento pendente de confirmação. Você receberá uma notificação quando for aprovado.');
    }
  }, [searchParams]);

  const verifyPayment = async (paymentId, status, externalRef) => {
    try {
      const response = await api.get(`/subscription/verify-payment?payment_id=${paymentId || ''}&status=${status || ''}&external_reference=${externalRef || ''}`);
      
      if (response.data.success) {
        setSuccess('Pagamento confirmado! Sua assinatura foi ativada com sucesso.');
        await refreshSubscription();
        setTimeout(() => navigate('/dashboard'), 3000);
      } else if (response.data.status === 'pending') {
        setSuccess('Pagamento pendente de confirmação.');
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
    }
  };

  const handleSubscribe = async (planId) => {
    setLoading(true);
    setSelectedPlan(planId);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/subscription/checkout', { plan_id: planId });
      
      if (response.data.mock_mode) {
        setSuccess('Assinatura criada com sucesso! (Modo demonstração)');
        await refreshSubscription();
        setTimeout(() => navigate('/dashboard'), 2000);
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.response?.data?.detail || 'Erro ao processar assinatura. Tente novamente.');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura?')) return;

    setLoading(true);
    setError('');

    try {
      await api.post('/subscription/cancel');
      setSuccess('Assinatura cancelada com sucesso.');
      await refreshSubscription();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cancelar assinatura.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-28" style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
      {/* Header */}
      <header
        className="px-4 py-5 flex items-center gap-4 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at top right, #0F766E 0%, #083D3A 60%, #0A0F14 100%)',
        }}
      >
        <div className="absolute inset-0 scanner-grid opacity-25 pointer-events-none" />
        <button 
          onClick={() => navigate(-1)}
          className="relative z-10 p-2 text-white/80 hover:text-cyan-300 transition-colors rounded-xl hover:bg-white/5"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="relative z-10">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-amber-300">Upgrade</p>
          <h1 className="font-bold text-xl text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            Planos Premium
          </h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-lg mx-auto">
        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm animate-fade-in-up">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl text-green-700 text-sm animate-fade-in-up">
            {success}
          </div>
        )}

        {/* Current Subscription */}
        {subscription && (
          <section className="mb-6 animate-fade-in-up">
            <div className="bg-gradient-to-r from-[#2E5C55] to-[#3A7068] rounded-3xl p-6 text-white shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Crown className="h-6 w-6 text-[#D4A373]" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">Assinatura Ativa</p>
                  <p className="font-bold text-lg">
                    {plans.find(p => p.id === subscription.plan_id)?.name || subscription.plan_id}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm text-white/80 mb-4 p-3 bg-white/10 rounded-xl">
                <span>Próxima cobrança:</span>
                <span className="font-medium">{new Date(subscription.next_billing_date).toLocaleDateString('pt-BR')}</span>
              </div>
              
              <Button 
                onClick={handleCancel}
                variant="outline"
                className="w-full bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full h-12"
                disabled={loading}
                data-testid="cancel-subscription-btn"
              >
                Cancelar assinatura
              </Button>
            </div>
          </section>
        )}

        {/* Section Header */}
        <div className="text-center mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h2 className="font-bold text-2xl text-[#1C1C1E] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            {subscription ? 'Trocar de plano' : 'Escolha seu plano'}
          </h2>
          <p className="text-[#8E8E93]">
            Desbloqueie todo o potencial da reflexologia
          </p>
        </div>

        {/* Plans Grid */}
        <div className="space-y-4">
          {plans.map((plan, index) => {
            const isCurrentPlan = subscription?.plan_id === plan.id;
            const PlanIcon = plan.icon;
            
            return (
              <div 
                key={plan.id}
                className={`relative bg-white rounded-3xl overflow-hidden transition-all animate-fade-in-up ${
                  plan.popular 
                    ? 'shadow-2xl ring-2 ring-[#D4A373]' 
                    : 'shadow-lg border border-[#E8E4D9]/50'
                } ${isCurrentPlan ? 'ring-2 ring-[#4DB6AC]' : ''}`}
                style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                data-testid={`plan-card-${plan.id}`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-[#D4A373] to-[#C49363] text-white text-xs font-bold px-4 py-2 rounded-bl-2xl">
                    MAIS POPULAR
                  </div>
                )}
                
                {/* Current Badge */}
                {isCurrentPlan && (
                  <div className="absolute top-0 left-0 bg-[#4DB6AC] text-white text-xs font-bold px-4 py-2 rounded-br-2xl">
                    ATUAL
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: `${plan.color}15` }}
                      >
                        <PlanIcon className="h-6 w-6" style={{ color: plan.color }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                          {plan.name}
                        </h3>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-bold text-[#2E5C55]">R$ {plan.priceDisplay}</span>
                      <span className="text-sm text-[#8E8E93]">{plan.period}</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-sm">
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${plan.color}15` }}
                        >
                          <Check className="h-3 w-3" style={{ color: plan.color }} />
                        </div>
                        <span className="text-[#48484A]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading || isCurrentPlan}
                    className={`w-full h-14 rounded-full font-semibold text-base transition-all ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-[#D4A373] to-[#C49363] hover:from-[#C49363] hover:to-[#B08353] text-white shadow-lg hover:shadow-xl' 
                        : 'bg-gradient-to-r from-[#2E5C55] to-[#3A7068] hover:from-[#234744] hover:to-[#2E5C55] text-white'
                    }`}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    {loading && selectedPlan === plan.id ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : isCurrentPlan ? (
                      'Plano atual'
                    ) : (
                      'Assinar agora'
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Section */}
        <section className="mt-8 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <div className="bg-white rounded-2xl p-5 flex items-center gap-4" style={{ border: '1px solid rgba(15,118,110,0.12)' }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                 style={{ background: 'rgba(34,211,238,0.1)' }}>
              <Shield className="h-6 w-6" style={{ color: '#0F766E' }} />
            </div>
            <div>
              <p className="font-bold text-[#0A0F14] mb-1">Pagamento seguro</p>
              <p className="text-sm text-[#6B7280]">
                Processado pelo Mercado Pago. Cancele quando quiser.
              </p>
            </div>
          </div>
        </section>
      </main>
      <FloatingNav />
    </div>
  );
};

export default PlansPage;
